import React, { useEffect, useState, useRef } from 'react';
import { useTour } from '../../contexts/TourContext';
import TourTooltip from './TourTooltip';

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export default function TourOverlay() {
  const { tour, endTour } = useTour();
  const [targetPosition, setTargetPosition] = useState<ElementPosition | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const currentStep = tour.steps[tour.currentStep];

  useEffect(() => {
    if (!tour.isActive) return;

    const updatePosition = () => {
      const targetElement = document.querySelector(currentStep.target);
      if (!targetElement) {
        console.warn(`Tour target element not found: ${currentStep.target}`);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      setTargetPosition({
        top: rect.top + scrollTop + rect.height / 2,
        left: rect.left + scrollLeft + rect.width / 2,
        width: rect.width,
        height: rect.height
      });

      // Scroll target into view if needed
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    };

    // Update position initially and on resize/scroll
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [tour.isActive, tour.currentStep, currentStep]);

  if (!tour.isActive || !targetPosition) return null;

  // Calculate spotlight position
  const spotlightStyle = {
    top: targetPosition.top - targetPosition.height / 2 - 10,
    left: targetPosition.left - targetPosition.width / 2 - 10,
    width: targetPosition.width + 20,
    height: targetPosition.height + 20
  };

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-50 pointer-events-none"
    >
      {/* Transparent overlay - no background color */}
      <div 
        className="absolute inset-0 pointer-events-auto"
        onClick={endTour}
      />

      {/* Spotlight with highlight border */}
      <div 
        className="absolute rounded-lg pointer-events-auto"
        style={spotlightStyle}
      >
        {/* Highlight border */}
        <div className="absolute inset-0 border-2 border-primary-500 rounded-lg animate-pulse" />
      </div>

      {/* Tooltip */}
      <div className="pointer-events-auto">
        <TourTooltip
          step={tour.currentStep}
          totalSteps={tour.steps.length}
          title={currentStep.title}
          content={currentStep.content}
          position={{
            top: targetPosition.top,
            left: targetPosition.left
          }}
          placement={currentStep.placement || 'bottom'}
        />
      </div>
    </div>
  );
}